import React, { useState } from 'react';
import DemographicFieldGroup from './DemographicFormField';
import { createDraft, submitDraft } from '../lib/demographic';

/**
 * DemographicSubmissionForm
 *
 * Field set is stable: first_name, last_name, date_of_birth, sex,
 * external_mrn are confirmed columns on the approved `patient` table
 * (IVC-DBS-002 v2.1). This form's shape does not depend on the pending
 * governance decision — only the SUBMIT TARGET does.
 *
 * IMPORTANT — governance dependency:
 * handleSubmit is stubbed. It must not write to `patient` directly
 * (date_of_birth is NOT NULL there and patient is the canonical,
 * post-verification entity — writing here would bypass the verification
 * workflow entirely). Once the demographic-submission entity is approved,
 * wire this to that table/endpoint instead.
 *
 * No submission target is called yet — submit is a local-state stub.
 */

const initialState = {
  first_name: '',
  last_name: '',
  date_of_birth: '',
  sex: '',
  external_mrn: '',
};

// Prefill first/last name from the signup-captured user object where
// available. These are display fields the patient can still edit/correct
// before submitting — signup capture isn't guaranteed to be accurate or
// complete (e.g. legal name mismatch), so this is a convenience default,
// not a substitute for the patient confirming their own demographics.
function buildInitialValues(currentUser) {
  return {
    ...initialState,
    first_name: currentUser?.first_name ?? '',
    last_name: currentUser?.last_name ?? '',
  };
}

export default function DemographicSubmissionForm({ currentUser, onSubmitted }) {
  const [values, setValues] = useState(() => buildInitialValues(currentUser));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  function handleChange(field, value) {
    setValues((prev) => ({ ...prev, [field]: value }));
  }

  function validate() {
    const next = {};
    if (!values.first_name.trim()) next.first_name = 'First name is required.';
    if (!values.last_name.trim()) next.last_name = 'Last name is required.';
    if (!values.date_of_birth) {
      next.date_of_birth = 'Date of birth is required.';
    } else {
      const dob = new Date(values.date_of_birth);
      const today = new Date();
      if (dob > today) next.date_of_birth = 'Date of birth cannot be in the future.';
    }
    // sex and external_mrn are nullable on `patient` — no required validation.
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const draft = await createDraft(values);
      await submitDraft(draft.id);
      onSubmitted?.(values);
    } catch (err) {
      const message = err.response?.data?.message || 'Submission failed. Please try again.';
      setErrors((prev) => ({ ...prev, _form: message }));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <DemographicFieldGroup
        label="First name"
        name="first_name"
        value={values.first_name}
        onChange={(v) => handleChange('first_name', v)}
        error={errors.first_name}
        required
      />
      <DemographicFieldGroup
        label="Last name"
        name="last_name"
        value={values.last_name}
        onChange={(v) => handleChange('last_name', v)}
        error={errors.last_name}
        required
      />
      <DemographicFieldGroup
        label="Date of birth"
        name="date_of_birth"
        type="date"
        value={values.date_of_birth}
        onChange={(v) => handleChange('date_of_birth', v)}
        error={errors.date_of_birth}
        required
      />
      <DemographicFieldGroup
        label="Sex"
        name="sex"
        type="select"
        options={['Female', 'Male', 'Other', 'Prefer not to say']}
        value={values.sex}
        onChange={(v) => handleChange('sex', v)}
        error={errors.sex}
      />
      <DemographicFieldGroup
        label="External MRN (if known)"
        name="external_mrn"
        value={values.external_mrn}
        onChange={(v) => handleChange('external_mrn', v)}
        error={errors.external_mrn}
      />

      {errors._form && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {errors._form}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full mt-2 rounded-lg bg-blue-600 text-white font-medium py-2.5 px-4 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
      >
        {submitting ? 'Submitting…' : 'Submit'}
      </button>
    </form>
  );
}